import {useUnsafeInternalFluxContext} from './useFluxContext';
import {type TFlux} from '../types';

/**
 * Creates a typed dispatch hook that provides type safety for dispatched actions.
 * You should create one hook and use it throughout your application or subsystem.
 *
 * @type SpecificActions - The action types that can be dispatched
 * @returns A React hook that returns a typed dispatch function
 *
 * @example
 * ```tsx
 * // Define your action types
 * type IncrementAction = { type: 'INCREMENT' };
 * type DecrementAction = { type: 'DECREMENT' };
 * type CounterActions = IncrementAction | DecrementAction;
 *
 * // Create a typed dispatch hook
 * export const useCounterDispatch = createUseDispatch<CounterActions>();
 *
 * // Use in your components
 * function CounterComponent() {
 *   const dispatch = useCounterDispatch();
 *
 *   return (
 *     <div>
 *       <button onClick={() => dispatch({ type: 'INCREMENT' })}>+</button>
 *       <button onClick={() => dispatch({ type: 'DECREMENT' })}>-</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function createUseDispatch<
  SpecificActions extends {type: string} = {type: string},
>() {
  function useTypedDispatch() {
    const flux_ = useUnsafeInternalFluxContext() as TFlux<any, SpecificActions>;
    return flux_.dispatch;
  }

  return useTypedDispatch;
}
